import { FacilityRcraSubpartPActivity } from "encamp-prisma/generated/browser";

/**
 * Where 40 CFR 266 subpart P (hazardous waste pharmaceuticals) is in effect.
 *
 * Sourced from EPA's state adoption table, "Where are the Management Standards
 * for Hazardous Waste Pharmaceuticals and Amendment to the P075 Listing for
 * Nicotine in Effect?", read 2026-08-04. Two groups are included:
 *
 *   - The 39 states and DC that EPA records as having adopted the rule.
 *   - The six jurisdictions EPA administers directly (AK, AS, IA, MP, PR, VI),
 *     where the federal rules apply without a state adoption step.
 *
 * Deliberately NOT the same list as `STATES_WITH_SUBPART_P` in
 * `rcraSectionAvailability`, which drives form rendering and excludes states
 * that do not file through RCRAInfo. The two answer different questions and
 * they diverge: Texas adopted the rule effective 2022-02-03 (TCEQ, 30 TAC
 * ch. 335) and requires the §11d box on the federal 8700-12, but files it by
 * mail rather than through RCRAInfo, so it is absent from the rendering list.
 * Reading that absence as non-adoption denied the exemption to every
 * pharmaceutical line in Texas and Maine, which is the bug this module fixes.
 *
 * Non-adopting as of the same reading: AR, CA, CT, GU, KS, MA, MN, NE, NV, NY,
 * RI. Pharmaceutical waste there is fully regulated hazardous waste and keeps
 * the ordinary generator treatment.
 *
 * This list decides whether waste appears on a federal biennial report and in
 * the generator-status count. Re-read the EPA table before editing it, and
 * record the read date above.
 */
const STATES_THAT_ADOPTED_SUBPART_P = new Set([
  "AK",
  "AL",
  "AS",
  "AZ",
  "CO",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KY",
  "LA",
  "MD",
  "ME",
  "MI",
  "MO",
  "MP",
  "MS",
  "MT",
  "NC",
  "ND",
  "NH",
  "NJ",
  "NM",
  "OH",
  "OK",
  "OR",
  "PA",
  "PR",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VI",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
]);

/** Whether 40 CFR 266 subpart P is in effect in the given state or territory. */
export const stateAdoptedSubpartP = (state: string | null | undefined): boolean =>
  !!state && STATES_THAT_ADOPTED_SUBPART_P.has(state.trim().toUpperCase());

/**
 * The profile columns the participation predicate reads: the §11d activity pair
 * and the §4 site-location state the activity was notified under. Structural
 * rather than `Pick<FacilityRcraProfile, …>` so callers can pass a narrow
 * `select` result.
 */
export type SubpartPProfileFields = {
  subpartPActivity?: FacilityRcraSubpartPActivity | null;
  subpartPWithdrawal?: boolean | null;
  rcraState?: string | null;
};

/**
 * Whether a facility manages its pharmaceutical waste under the
 * healthcare-facility standards of 40 CFR 266 subpart P — the single predicate
 * every consumer reads, so the pharmaceutical rules cannot drift apart.
 * Sibling of `isUniversalWasteFederallyExempt` in the compliance engine, which
 * plays the same role for universal waste.
 *
 * Three conditions, all required:
 *
 *   1. A §11d activity of `HEALTHCARE_FACILITY`. Subpart P splits its
 *      population in two and only this half gets the treatment the classifier
 *      rules implement — see the reverse-distributor note below. The rule is
 *      mandatory for SQG and LQG healthcare facilities where it is in effect,
 *      so a notified activity means the facility operates under it
 *      (266.501(d): the subpart applies "in lieu of parts 262 through 265").
 *   2. No withdrawal. Withdrawing ends the management regime even though the
 *      historical activity value stays recorded.
 *   3. The rule is in effect in the state, per `stateAdoptedSubpartP`.
 *
 * **Reverse distributors are deliberately excluded.** 266.501(e) subjects them
 * to 266.505 through 266.510 instead, a different regime with its own
 * reporting duties: 266.510(c)(9)(i) requires a reverse distributor shipping
 * evaluated hazardous waste pharmaceuticals off site to file its own biennial
 * report. Treating a `REVERSE_DISTRIBUTOR` activity as operating under the
 * healthcare-facility standards would drop regulated shipments off that report.
 *
 * The state tested is the §4 RCRA site-location state, falling back to the
 * facility's address state when the profile leaves it blank — the resolution
 * `resolveSiteLocation` already applies to build the 8700-12
 * (`rcraState ?? facility.state`). A blank `rcraState` is missing data, not a
 * statement that the facility is outside the program. The fallback is
 * near-theoretical today (6 of 837 profiles carrying an activity have no
 * `rcraState`, and none of the 837 disagrees with its facility's state), so it
 * exists to keep this predicate answering the same question the 8700-12 does
 * rather than to serve a known population.
 *
 * An absent profile is `false`: never having filed is not evidence of
 * operating under the program, and over-reporting is the safe direction.
 */
export const facilityOperatesUnderSubpartP = (
  profile: SubpartPProfileFields | null | undefined,
  /** The facility's address state, used only when `profile.rcraState` is blank. */
  facilityState: string | null | undefined
): boolean =>
  profile?.subpartPActivity === FacilityRcraSubpartPActivity.HEALTHCARE_FACILITY &&
  profile.subpartPWithdrawal !== true &&
  // `||` rather than `??`: a whitespace-only rcraState is blank, and `??` would
  // keep it and skip the fallback the docstring promises.
  stateAdoptedSubpartP(profile.rcraState?.trim() || facilityState);
